/** @jsxImportSource react */
import React from 'react'
import { Toaster as HotToaster, toast as hotToast } from 'react-hot-toast'
import {
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  X,
  Loader2,
  Bell,
} from 'lucide-react'

// Enhanced toast API with modern styling
export const toast = {
  success: (message: string) =>
    hotToast.custom((t) => (
      <div
        className={`${t.visible ? 'animate-enter' : 'animate-leave'} 
        max-w-md w-full bg-white shadow-lg rounded-lg flex overflow-hidden`}
      >
        <div className="w-2 bg-primary-500 flex-shrink-0" />
        <div className="flex-1 p-4 flex">
          <div className="flex-shrink-0 mr-3">
            <CheckCircle className="h-5 w-5 text-primary-500" />
          </div>
          <div className="flex-1 pr-2">
            <p className="text-sm font-medium text-gray-900">{message}</p>
          </div>
          <button
            onClick={() => hotToast.dismiss(t.id)}
            className="ml-4 flex-shrink-0 self-start text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    )),
  error: (message: string) =>
    hotToast.custom((t) => (
      <div
        className={`${t.visible ? 'animate-enter' : 'animate-leave'} 
        max-w-md w-full bg-white shadow-lg rounded-lg flex overflow-hidden`}
      >
        <div className="w-2 bg-secondary-800 flex-shrink-0" />
        <div className="flex-1 p-4 flex">
          <div className="flex-shrink-0 mr-3">
            <AlertCircle className="h-5 w-5 text-secondary-800" />
          </div>
          <div className="flex-1 pr-2">
            <p className="text-sm font-medium text-gray-900">{message}</p>
          </div>
          <button
            onClick={() => hotToast.dismiss(t.id)}
            className="ml-4 flex-shrink-0 self-start text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    )),
  loading: (message: string) =>
    hotToast.custom((t) => (
      <div
        className={`${t.visible ? 'animate-enter' : 'animate-leave'} 
        max-w-md w-full bg-white shadow-lg rounded-lg flex overflow-hidden`}
      >
        <div className="w-2 bg-primary-500 flex-shrink-0" />
        <div className="flex-1 p-4 flex">
          <div className="flex-shrink-0 mr-3">
            <Loader2 className="h-5 w-5 text-primary-500 animate-spin" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">{message}</p>
          </div>
        </div>
      </div>
    )),
  info: (message: string) =>
    hotToast.custom((t) => (
      <div
        className={`${t.visible ? 'animate-enter' : 'animate-leave'} 
        max-w-md w-full bg-white shadow-lg rounded-lg flex overflow-hidden`}
      >
        <div className="w-2 bg-primary-400 flex-shrink-0" />
        <div className="flex-1 p-4 flex">
          <div className="flex-shrink-0 mr-3">
            <Info className="h-5 w-5 text-primary-400" />
          </div>
          <div className="flex-1 pr-2">
            <p className="text-sm font-medium text-gray-900">{message}</p>
          </div>
          <button
            onClick={() => hotToast.dismiss(t.id)}
            className="ml-4 flex-shrink-0 self-start text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    )),
  warning: (message: string) =>
    hotToast.custom((t) => (
      <div
        className={`${t.visible ? 'animate-enter' : 'animate-leave'} 
        max-w-md w-full bg-white shadow-lg rounded-lg flex overflow-hidden`}
      >
        <div className="w-2 bg-secondary-500 flex-shrink-0" />
        <div className="flex-1 p-4 flex">
          <div className="flex-shrink-0 mr-3">
            <AlertTriangle className="h-5 w-5 text-secondary-500" />
          </div>
          <div className="flex-1 pr-2">
            <p className="text-sm font-medium text-gray-900">{message}</p>
          </div>
          <button
            onClick={() => hotToast.dismiss(t.id)}
            className="ml-4 flex-shrink-0 self-start text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    )),
  notification: (message: string, description?: string) =>
    hotToast.custom((t) => (
      <div
        className={`${t.visible ? 'animate-enter' : 'animate-leave'} 
        max-w-md w-full bg-white shadow-lg rounded-lg flex overflow-hidden`}
      >
        <div className="w-2 bg-primary-500 flex-shrink-0" />
        <div className="flex-1 p-4 flex">
          <div className="flex-shrink-0 mr-3">
            <Bell className="h-5 w-5 text-primary-500" />
          </div>
          <div className="flex-1 pr-2">
            <p className="text-sm font-medium text-gray-900">{message}</p>
            {description && (
              <p className="mt-1 text-sm text-gray-500">{description}</p>
            )}
          </div>
          <button
            onClick={() => hotToast.dismiss(t.id)}
            className="ml-4 flex-shrink-0 self-start text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    )),
  dismiss: hotToast.dismiss,
  remove: hotToast.remove,
}

export const Toaster: React.FC = () => {
  return (
      <HotToaster
        position="top-right"
        reverseOrder={false}
        gutter={12}
        containerClassName=""
        containerStyle={{
          top: 20,
          right: 20,
        }}
        toastOptions={{
          duration: 4000,
          style: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0,
            maxWidth: '100%',
            width: 'auto',
          },
        }}
      >
        {(t) => <>{t.message}</>}
      </HotToaster>
  )
}